import React from 'react';
import { format } from 'date-fns';
import { GiftIcon } from '@heroicons/react/24/outline';
import { useImageUrl } from '../../services/uploads';
import { usePrizeClaims } from '../../hooks/learner/usePrizes';
import type { LearnerPrizeClaim } from '../../types/learnerPrizes';
import type { ClaimStatus } from '../../types/gamification';

const STATUS_LABEL: Record<ClaimStatus, string> = {
  pending: 'Pending',
  fulfilled: 'Fulfilled',
  cancelled: 'Canceled',
};

const STATUS_TONE: Record<ClaimStatus, string> = {
  pending: 'bg-warning-500/15 text-warning-600 dark:text-warning-300',
  fulfilled: 'bg-success-500/15 text-success-600 dark:text-success-300',
  cancelled: 'bg-text-tertiary/15 text-text-tertiary',
};

/**
 * Inline row of the caller's most recent claims rendered at the bottom of
 * `/learn/prizes`. Hides itself when the learner has no claims.
 */
export default function PrizeClaimsRow() {
  const { claims, isLoading, isError } = usePrizeClaims({ perPage: 6 });
  const recent = claims.slice(0, 6);

  if (isLoading) return <ClaimsRowSkeleton />;
  if (isError) return null;
  if (recent.length === 0) return null;

  return (
    <section className="space-y-3" aria-label="My Recent Claims">
      <header className="flex items-end justify-between">
        <h2 className="text-lg font-bold text-text-primary tracking-tight">My Claims</h2>
        <span className="text-xs text-text-tertiary">
          {recent.length === 1 ? '1 reward' : `${recent.length} rewards`}
        </span>
      </header>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {recent.map((claim) => (
          <ClaimCard key={claim.id} claim={claim} />
        ))}
      </div>
    </section>
  );
}

function ClaimCard({ claim }: { claim: LearnerPrizeClaim }) {
  const [coverUrl] = useImageUrl(claim.prize?.media_urls?.[0] ?? null);
  const tone = STATUS_TONE[claim.status];
  const label = STATUS_LABEL[claim.status];
  const claimedAt = claim.claimed_at ?? claim.created_at;

  return (
    <article
      className="snap-start shrink-0 w-[220px] rounded-2xl border border-border-primary/20 bg-background-primary dark:bg-grey-800 overflow-hidden"
    >
      <div className="relative aspect-[4/3] bg-text-tertiary/5">
        {coverUrl ? (
          <img src={coverUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div
            aria-hidden="true"
            className="absolute inset-0 flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, rgb(var(--color-primary) / 0.18) 0%, rgb(var(--color-secondary) / 0.18) 100%)',
            }}
          >
            <GiftIcon className="w-8 h-8 text-primary-500/60" />
          </div>
        )}
        <span
          className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${tone}`}
        >
          {label}
        </span>
      </div>
      <div className="p-3 space-y-1">
        <p className="text-sm font-semibold text-text-primary line-clamp-1">
          {claim.prize?.title ?? 'Prize'}
        </p>
        <p className="text-xs text-text-tertiary tabular-nums">
          {claim.points_spent.toLocaleString()} pts
          {claimedAt ? ` · ${format(new Date(claimedAt), 'MMM d, yyyy')}` : ''}
        </p>
      </div>
    </article>
  );
}

function ClaimsRowSkeleton() {
  return (
    <section className="space-y-3" aria-hidden="true">
      <div className="h-5 w-28 rounded bg-text-tertiary/15 animate-pulse" />
      <div className="flex gap-3">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="shrink-0 w-[220px] rounded-2xl border border-border-primary/10 overflow-hidden animate-pulse"
          >
            <div className="aspect-[4/3] bg-text-tertiary/10" />
            <div className="p-3 space-y-2">
              <div className="h-3 w-3/4 rounded bg-text-tertiary/10" />
              <div className="h-3 w-1/2 rounded bg-text-tertiary/10" />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
